import type { TopologyPayload } from "./types/topology";


export type Selection =
  | { kind: "seat"; seatId: string }
  // eX3 NC click and eX1 MCU click are treated alike.
  | { kind: "nc"; id: number }
  | { kind: "port"; nc: number; port: number }
  | { kind: "cs"; id: number }
  | { kind: "fs"; idx: number }
  | { kind: "ai"; idx: number }
  // 767 Area Distribution Box — num matches the designation ("ADB 002" → 2).
  | { kind: "adb"; num: number }
  // Data-side port on a chain parent (FS/ADB/IS — kind is fleet-uniform).
  | { kind: "dport"; num: number; port: number }
  // Out-port socket on a top-tier unit ("AI 1 · P12", "IS 1 · P16") —
  // selects everything downstream of that specific port.
  | { kind: "aiport"; unit: string; num: number; port: number }
  // Availability view: a fault incident from the demo trip. idx indexes the
  // current trip's incidents list; seat dimming is driven by the incident's
  // seat set (FleetPage passes it down as brightSeats), not by topology.
  | { kind: "incident"; idx: number };


export type HighlightTarget =
  | { kind: "seat"; seatId: string }
  | { kind: "nc"; id: number }
  | { kind: "port"; nc: number; port: number }
  | { kind: "cs"; id: number }
  | { kind: "fs"; idx: number }
  | { kind: "ai"; idx: number }
  | { kind: "adb"; num: number }
  | { kind: "dport"; num: number; port: number }
  | { kind: "aiport"; unit: string; num: number; port: number }
  // Terminal branch (CT / CP / MAS): carries its own parent triple so it
  // only lights with that exact parent unit / port.
  | { kind: "leaf"; unit: string; num: number; port: number | null }
  | { kind: "connector"; conn: "cs" | "port" | "hop" | "sub" | "chain" | "link" | "power"; nc?: number; port?: number };


// ---- URL codec ------------------------------------------------------------
// Selections encode into the ?sel= query param (seat:39G / dport:2-4 / …).
// Every click pushes a history entry, so browser back/forward retraces the
// interaction trail and any state doubles as a shareable link.

export function encodeSelection(s: Selection): string {
  switch (s.kind) {
    case "seat":  return `seat:${s.seatId}`;
    case "nc":    return `nc:${s.id}`;
    case "port":  return `port:${s.nc}-${s.port}`;
    case "cs":    return `cs:${s.id}`;
    case "fs":    return `fs:${s.idx}`;
    case "ai":    return `ai:${s.idx}`;
    case "adb":   return `adb:${s.num}`;
    case "dport": return `dport:${s.num}-${s.port}`;
    // Unit is a designation prefix ("AI", "IS_ARM") — never contains "~".
    case "aiport": return `aiport:${s.unit}~${s.num}-${s.port}`;
    case "incident": return `incident:${s.idx}`;
  }
}

export function decodeSelection(raw: string | null): Selection | undefined {
  if (!raw) return undefined;
  const [kind, arg] = raw.split(":");
  if (!arg) return undefined;
  const pair = (): [number, number] | null => {
    const m = arg.match(/^(\d+)-(\d+)$/);
    return m ? [parseInt(m[1], 10), parseInt(m[2], 10)] : null;
  };
  const num = /^\d+$/.test(arg) ? parseInt(arg, 10) : null;
  switch (kind) {
    case "seat":  return /^\d+[A-Z]$/i.test(arg)
      ? { kind: "seat", seatId: arg.toUpperCase() } : undefined;
    case "nc":    return num !== null ? { kind: "nc", id: num } : undefined;
    case "cs":    return num !== null ? { kind: "cs", id: num } : undefined;
    case "fs":    return num !== null ? { kind: "fs", idx: num } : undefined;
    case "ai":    return num !== null ? { kind: "ai", idx: num } : undefined;
    case "adb":   return num !== null ? { kind: "adb", num } : undefined;
    case "incident": return num !== null ? { kind: "incident", idx: num } : undefined;
    case "port": {
      const p = pair();
      return p ? { kind: "port", nc: p[0], port: p[1] } : undefined;
    }
    case "dport": {
      const p = pair();
      return p ? { kind: "dport", num: p[0], port: p[1] } : undefined;
    }
    case "aiport": {
      const m = arg.match(/^(.+)~(\d+)-(\d+)$/);
      return m
        ? { kind: "aiport", unit: m[1],
            num: parseInt(m[2], 10), port: parseInt(m[3], 10) }
        : undefined;
    }
    default: return undefined;
  }
}

/** A URL may reference a seat or chain another fleet doesn't have —
 *  applying it would dim everything with nothing highlighted. */
export function isValidSelection(s: Selection, topology: TopologyPayload): boolean {
  if (s.kind === "seat") return s.seatId in topology.seats;
  if (s.kind === "dport") {
    return Object.values(topology.seats).some(
      (seat) => seat.chain_num === s.num && seat.chain_port === s.port,
    );
  }
  return true;
}


/** Uplink triple from lru_lookup's Server_Type/Number/Port (primary rows
 *  only — redundant iLOPA_Flag links never drive highlighting). */
interface Uplink {
  type: string;
  num: number;
  port: number | null;
}

const numOf = (designation: string): number =>
  parseInt(designation.match(/(\d+)\s*$/)?.[1] ?? "0", 10);

const isAiType = (t: string): boolean => t.toUpperCase().startsWith("AI");
const isServerType = (t: string): boolean => {
  const u = t.toUpperCase();
  return u.startsWith("FS") || u.startsWith("IS");
};

/** Chain server (FS n / IS n) → its parent unit, or null when unknown. */
export function serverUplink(
  topology: TopologyPayload,
  num: number,
): Uplink | null {
  for (const l of topology.lrus ?? []) {
    const a = l.acronym ?? "";
    const isServer =
      a.startsWith("FS") || (a.startsWith("IS") && !a.includes("HCS08"));
    if (!isServer || numOf(l.designation) !== num) continue;
    if (l.server_type && l.server_number !== null && l.server_number !== undefined) {
      return { type: l.server_type, num: l.server_number, port: l.server_port ?? null };
    }
  }
  return null;
}

/** MCU (0-based idx) → its power parent (e.g. AI 1 · P3, IS 1 · P16). */
export function mcuUplink(
  topology: TopologyPayload,
  mcuIdx: number,
): Uplink | null {
  for (const l of topology.lrus ?? []) {
    if (l.acronym !== "MCU") continue;
    if (numOf(l.designation) !== mcuIdx + 1) continue;
    if (l.server_type && l.server_number !== null && l.server_number !== undefined) {
      return { type: l.server_type, num: l.server_number, port: l.server_port ?? null };
    }
  }
  return null;
}

/** ADB number → its FS uplink (parent FS + which FS port feeds it). */
export function adbUplink(
  topology: TopologyPayload,
  adbNum: number,
): { fs: number | null; port: number | null } {
  for (const l of topology.lrus ?? []) {
    if (!l.acronym?.startsWith("ADB")) continue;
    const m = l.designation.match(/(\d+)\s*$/);
    if (m && parseInt(m[1], 10) === adbNum) {
      return l.server_type === "FS"
        ? { fs: l.server_number ?? null, port: l.server_port ?? null }
        : { fs: null, port: null };
    }
  }
  return { fs: null, port: null };
}

/** ADB number → parent FS number, from lru_lookup's Server_Type/Number. */
export function adbParentFs(topology: TopologyPayload, adbNum: number): number | null {
  return adbUplink(topology, adbNum).fs;
}


/** Structural equality — drives the click-again-to-step-up interaction. */
export function sameSelection(a: Selection, b: Selection): boolean {
  if (a.kind !== b.kind) return false;
  switch (a.kind) {
    case "seat":   return a.seatId === (b as typeof a).seatId;
    case "nc":     return a.id === (b as typeof a).id;
    case "cs":     return a.id === (b as typeof a).id;
    case "fs":     return a.idx === (b as typeof a).idx;
    case "ai":     return a.idx === (b as typeof a).idx;
    case "adb":    return a.num === (b as typeof a).num;
    case "port": {
      const o = b as typeof a;
      return a.nc === o.nc && a.port === o.port;
    }
    case "dport": {
      const o = b as typeof a;
      return a.num === o.num && a.port === o.port;
    }
    case "aiport": {
      const o = b as typeof a;
      return a.unit === o.unit && a.num === o.num && a.port === o.port;
    }
    case "incident": return a.idx === (b as typeof a).idx;
  }
}

/** Map an uplink triple to the selection of the parent UNIT. */
function uplinkSelection(u: Uplink | null): Selection | undefined {
  if (!u) return undefined;
  if (isAiType(u.type)) return { kind: "ai", idx: u.num - 1 };
  if (isServerType(u.type)) return { kind: "fs", idx: u.num - 1 };
  return undefined;
}

/**
 * One level UP the hierarchy chain — clicking an already-selected element
 * "de-selects" into its parent scope instead of clearing:
 *   dport (ADB 1 · P2) → ADB 1 → FS x → AI n → (root: clear).
 * View-aware for seats and MCUs (data chain vs power path).
 */
export function parentSelection(
  sel: Selection,
  topology: TopologyPayload,
  view: "data" | "mcu" = "data",
): Selection | undefined {
  const isEx3 = topology.layout.topology.type === "nc-port";
  switch (sel.kind) {
    case "seat": {
      const seat = topology.seats[sel.seatId];
      if (!seat) return undefined;
      if (isEx3) {
        return seat.nc !== undefined && seat.nc !== null
          ? { kind: "port", nc: seat.nc, port: seat.port }
          : undefined;
      }
      if (view === "mcu") {
        return seat.mcu !== undefined && seat.mcu !== null
          ? { kind: "port", nc: seat.mcu, port: seat.port }
          : undefined;
      }
      return seat.chain_num !== undefined && seat.chain_port !== undefined
        ? { kind: "dport", num: seat.chain_num, port: seat.chain_port }
        : undefined;
    }
    case "dport": {
      // Chain kind comes from the seats riding the port (FS/ADB/IS-uniform).
      const rider = Object.values(topology.seats).find(
        (s) => s.chain_num === sel.num && s.chain_port === sel.port,
      );
      return rider?.chain_kind === "ADB"
        ? { kind: "adb", num: sel.num }
        : { kind: "fs", idx: sel.num - 1 };
    }
    case "adb": {
      const fs = adbParentFs(topology, sel.num);
      return fs !== null ? { kind: "fs", idx: fs - 1 } : undefined;
    }
    case "aiport":
      // The port's own unit — all its ports light up.
      return isAiType(sel.unit)
        ? { kind: "ai", idx: sel.num - 1 }
        : { kind: "fs", idx: sel.num - 1 };
    case "fs":
      return uplinkSelection(serverUplink(topology, sel.idx + 1));
    case "ai": {
      // Daisy-chained AI (75Y: AI 002 ← AI 1) steps to its peer; roots clear.
      for (const l of topology.lrus ?? []) {
        const a = l.acronym ?? "";
        if (!a.startsWith("AI") || a.includes("HCS08")) continue;
        if (numOf(l.designation) !== sel.idx + 1) continue;
        if (l.server_type && isAiType(l.server_type) &&
            l.server_number !== null && l.server_number !== undefined) {
          return { kind: "ai", idx: l.server_number - 1 };
        }
      }
      return undefined;
    }
    case "port":
      // Power/NC port → its MCU / NC.
      return { kind: "nc", id: sel.nc };
    case "nc":
      // eX1 MCU → its power parent (AI n · P3); eX3 NC feeds from BOTH CS
      // (mesh) — no single parent, so clear.
      return isEx3 ? undefined : uplinkSelection(mcuUplink(topology, sel.id));
    case "cs":
      return undefined;
    case "incident":
      // Incidents live in the availability panel (checkbox multi-select) —
      // there is no hierarchy above them, a re-click just clears.
      return undefined;
  }
}


/** Returns true if `target` should remain at full opacity given `sel`.
 *  `view` disambiguates seat brightness for AI / AI-port selections: the
 *  Data view follows the data chain, the MCU view follows the power path. */
export function isHighlighted(
  sel: Selection | undefined,
  target: HighlightTarget,
  topology: TopologyPayload,
  view: "data" | "mcu" = "data",
): boolean {
  if (!sel) return true;
  // Incident selection dims seats via the incident's own seat list
  // (brightSeats override in SeatGrid) — topology stays fully lit.
  if (sel.kind === "incident") return true;

  const isEx3 = topology.layout.topology.type === "nc-port";
  const seatNc = (s: TopologyPayload["seats"][string]) =>
    isEx3 ? (s.nc ?? -1) : (s.mcu ?? -1);

  // ---- what the selection implies ---------------------------------------

  let selNc: number | undefined;
  let selPort: number | undefined;
  let selSeatId: string | undefined;
  if (sel.kind === "seat") {
    const seat = topology.seats[sel.seatId];
    if (seat) {
      selNc = seatNc(seat);
      selPort = seat.port;
      selSeatId = sel.seatId;
    }
  } else if (sel.kind === "nc") {
    selNc = sel.id;
  } else if (sel.kind === "port") {
    selNc = sel.nc;
    selPort = sel.port;
  }

  /** Chain-server (FS/IS) number the selection sits on, or null. */
  const selChainFs = (): number | null => {
    switch (sel.kind) {
      case "fs":    return sel.idx + 1;
      case "adb":   return adbParentFs(topology, sel.num);
      case "dport": return adbParentFs(topology, sel.num) ?? sel.num;
      case "seat": {
        const seat = topology.seats[sel.seatId];
        if (!seat?.chain_kind || seat.chain_num === undefined) return null;
        if (seat.chain_kind === "ADB") return adbParentFs(topology, seat.chain_num);
        return seat.chain_num;
      }
      default: return null;
    }
  };

  // ---- uplink matchers (null = unknown → caller decides the default) ----

  /** Does uplink `u` come from AI number `aiNum`? */
  const upFromAi = (u: Uplink | null, aiNum: number): boolean | null =>
    u === null ? null : isAiType(u.type) && u.num === aiNum;

  /** Does uplink `u` come out of the selected aiport triple? */
  const upFromPort = (
    u: Uplink | null,
    p: { unit: string; num: number; port: number },
  ): boolean | null =>
    u === null
      ? null
      : u.type.toUpperCase().startsWith(p.unit.toUpperCase()) &&
        u.num === p.num &&
        (u.port === null || u.port === p.port);

  /** Does uplink `u` come from chain server (FS/IS) number `n`? */
  const upFromServer = (u: Uplink | null, n: number): boolean | null =>
    u === null ? null : isServerType(u.type) && u.num === n;

  /** Combine two nullable verdicts: unknown-unknown stays permissive. */
  const anyOf = (...vs: Array<boolean | null>): boolean => {
    if (vs.every((v) => v === null)) return true;
    return vs.some((v) => v === true);
  };

  /** Uplink of the seat's data-chain server, or null. */
  const seatChainUplink = (seat: TopologyPayload["seats"][string]): Uplink | null => {
    if (!seat.chain_kind || seat.chain_num === undefined) return null;
    const fs = seat.chain_kind === "ADB"
      ? adbParentFs(topology, seat.chain_num)
      : seat.chain_num;
    return fs === null ? null : serverUplink(topology, fs);
  };

  // ---- target rules ------------------------------------------------------

  switch (target.kind) {

    case "ai": {
      const aiNum = target.idx + 1;
      switch (sel.kind) {
        case "ai":     return sel.idx === target.idx;
        case "aiport": return isAiType(sel.unit) && sel.num === aiNum;
        case "cs":     return true;
        case "fs": case "adb": case "dport": {
          const fs = selChainFs();
          if (fs === null) return true;
          return upFromAi(serverUplink(topology, fs), aiNum) ?? true;
        }
        case "seat": {
          const seat = topology.seats[sel.seatId];
          if (!seat) return true;
          return anyOf(
            upFromAi(seatChainUplink(seat), aiNum),
            seat.mcu !== undefined && seat.mcu !== null
              ? upFromAi(mcuUplink(topology, seat.mcu), aiNum)
              : null,
          );
        }
        case "nc":   return upFromAi(mcuUplink(topology, sel.id), aiNum) ?? true;
        case "port": return upFromAi(mcuUplink(topology, sel.nc), aiNum) ?? true;
      }
      return true;
    }

    case "aiport": {
      switch (sel.kind) {
        case "aiport":
          return sel.unit === target.unit && sel.num === target.num &&
                 sel.port === target.port;
        case "ai":
          return isAiType(target.unit) && target.num === sel.idx + 1;
        case "cs": return true;
        case "fs": {
          // The unit's own out-ports stay bright (IS 1 selected → its chips)…
          if (isServerType(target.unit) && target.num === sel.idx + 1) return true;
          // …and so does the exact upstream port that feeds it.
          return upFromPort(serverUplink(topology, sel.idx + 1), target) ?? true;
        }
        case "adb": case "dport": {
          const fs = selChainFs();
          if (fs === null) return true;
          return upFromPort(serverUplink(topology, fs), target) ?? true;
        }
        case "seat": {
          const seat = topology.seats[sel.seatId];
          if (!seat) return true;
          return anyOf(
            upFromPort(seatChainUplink(seat), target),
            seat.mcu !== undefined && seat.mcu !== null
              ? upFromPort(mcuUplink(topology, seat.mcu), target)
              : null,
          );
        }
        case "nc":   return upFromPort(mcuUplink(topology, sel.id), target) ?? true;
        case "port": return upFromPort(mcuUplink(topology, sel.nc), target) ?? true;
      }
      return true;
    }

    case "leaf": {
      switch (sel.kind) {
        case "ai":
          return isAiType(target.unit) && target.num === sel.idx + 1;
        case "aiport":
          return target.unit.toUpperCase().startsWith(sel.unit.toUpperCase()) &&
                 target.num === sel.num &&
                 (target.port === null || target.port === sel.port);
        case "fs":
          return isServerType(target.unit) && target.num === sel.idx + 1;
        case "cs": return true;
        // Not on any seat/ADB/MCU chain — dim for precise tracing.
        default: return false;
      }
    }

    case "fs": {
      const fsNum = target.idx + 1;
      switch (sel.kind) {
        case "fs":  return sel.idx === target.idx;
        case "adb": return adbParentFs(topology, sel.num) === fsNum;
        case "dport": {
          if (sel.num === fsNum) return true;
          return adbParentFs(topology, sel.num) === fsNum;
        }
        case "ai": {
          const u = serverUplink(topology, fsNum);
          return upFromAi(u, sel.idx + 1) ?? true;
        }
        case "aiport": {
          if (isServerType(sel.unit) && sel.num === fsNum) return true;
          return upFromPort(serverUplink(topology, fsNum), sel) ?? true;
        }
        case "seat": {
          const seat = topology.seats[sel.seatId];
          if (!seat) return true;
          if (seat.chain_kind === "FS" || seat.chain_kind === "IS") {
            return seat.chain_num === fsNum;
          }
          if (seat.chain_kind === "ADB" && seat.chain_num !== undefined) {
            const parent = adbParentFs(topology, seat.chain_num);
            if (parent !== null) return parent === fsNum;
          }
          return true;
        }
        default: return true;  // cs / nc / port — different tier or view
      }
    }

    case "adb": {
      switch (sel.kind) {
        case "adb":   return target.num === sel.num;
        case "dport": return target.num === sel.num;
        case "fs":
          return adbParentFs(topology, target.num) === sel.idx + 1;
        case "ai": {
          const fs = adbParentFs(topology, target.num);
          if (fs === null) return true;
          return upFromAi(serverUplink(topology, fs), sel.idx + 1) ?? true;
        }
        case "aiport": {
          const fs = adbParentFs(topology, target.num);
          if (fs === null) return true;
          return upFromPort(serverUplink(topology, fs), sel) ?? true;
        }
        case "seat": {
          const seat = topology.seats[sel.seatId];
          if (!seat || seat.chain_kind !== "ADB") return true;
          return seat.chain_num === target.num;
        }
        default: return true;
      }
    }

    case "dport": {
      switch (sel.kind) {
        case "dport":
          return target.num === sel.num && target.port === sel.port;
        case "adb": return target.num === sel.num;
        case "fs": {
          if (target.num === sel.idx + 1) return true;
          return adbParentFs(topology, target.num) === sel.idx + 1;
        }
        case "ai": {
          const fs = adbParentFs(topology, target.num) ?? target.num;
          return upFromAi(serverUplink(topology, fs), sel.idx + 1) ?? true;
        }
        case "aiport": {
          const fs = adbParentFs(topology, target.num) ?? target.num;
          return upFromPort(serverUplink(topology, fs), sel) ?? true;
        }
        case "seat": {
          const seat = topology.seats[sel.seatId];
          if (!seat || !seat.chain_kind) return true;
          return seat.chain_num === target.num && seat.chain_port === target.port;
        }
        default: return true;
      }
    }

    case "nc": {
      switch (sel.kind) {
        case "ai":
          return upFromAi(mcuUplink(topology, target.id), sel.idx + 1) ?? true;
        case "aiport":
          return upFromPort(mcuUplink(topology, target.id), sel) ?? true;
        case "fs":
          return upFromServer(mcuUplink(topology, target.id), sel.idx + 1) ?? true;
        case "adb": case "dport": case "cs": return true;
        default:
          return selNc === undefined || target.id === selNc;
      }
    }

    case "port": {
      switch (sel.kind) {
        case "ai":
          return upFromAi(mcuUplink(topology, target.nc), sel.idx + 1) ?? true;
        case "aiport":
          return upFromPort(mcuUplink(topology, target.nc), sel) ?? true;
        case "fs":
          return upFromServer(mcuUplink(topology, target.nc), sel.idx + 1) ?? true;
        case "adb": case "dport": case "cs": return true;
        default:
          if (selNc === undefined) return true;
          if (selPort !== undefined) {
            return target.nc === selNc && target.port === selPort;
          }
          return target.nc === selNc;
      }
    }

    case "cs":
      return sel.kind !== "cs" || target.id === sel.id;

    case "seat": {
      const seat = topology.seats[target.seatId];
      if (!seat) return false;
      switch (sel.kind) {
        case "seat":
          // Single-seat selection: only the chosen seat is bright — the rest
          // of the chain (boxes/connectors) is handled by the other targets.
          return target.seatId === selSeatId;
        case "dport": {
          if (!seat.chain_kind) return true;
          return seat.chain_num === sel.num && seat.chain_port === sel.port;
        }
        case "adb": {
          if (seat.chain_kind !== "ADB") return true;
          return seat.chain_num === sel.num;
        }
        case "fs": {
          if (seat.chain_kind === "FS" || seat.chain_kind === "IS") {
            return seat.chain_num === sel.idx + 1;
          }
          if (seat.chain_kind === "ADB" && seat.chain_num !== undefined) {
            const parent = adbParentFs(topology, seat.chain_num);
            if (parent !== null) return parent === sel.idx + 1;
          }
          return true;
        }
        case "ai": {
          const chain = upFromAi(seatChainUplink(seat), sel.idx + 1);
          const power = seat.mcu !== undefined && seat.mcu !== null
            ? upFromAi(mcuUplink(topology, seat.mcu), sel.idx + 1)
            : null;
          // Per-view precedence so seats never light while their own data
          // chain is dimmed (or vice versa in the MCU view).
          return view === "mcu" ? (power ?? chain ?? true) : (chain ?? power ?? true);
        }
        case "aiport": {
          const chain = upFromPort(seatChainUplink(seat), sel);
          const power = seat.mcu !== undefined && seat.mcu !== null
            ? upFromPort(mcuUplink(topology, seat.mcu), sel)
            : null;
          return view === "mcu" ? (power ?? chain ?? true) : (chain ?? power ?? true);
        }
        case "cs": return true;
        default: {
          if (selNc === undefined) return false;
          if (seatNc(seat) !== selNc) return false;
          return selPort === undefined || seat.port === selPort;
        }
      }
    }

    case "connector":
      if (target.conn === "cs") {
        if (selNc !== undefined) return target.nc === selNc;
        return true;
      }
      if (target.conn === "port") {
        if (selPort !== undefined) {
          return target.nc === selNc && target.port === selPort;
        }
        return target.nc === selNc;
      }
      if (target.conn === "hop") {
        if (selNc !== undefined) return target.nc === selNc;
        return false;
      }
      if (target.conn === "power") {
        // Per-MCU power cables trace back to the shared out-port socket.
        if (target.nc === undefined) return true;
        switch (sel.kind) {
          case "ai":
            return upFromAi(mcuUplink(topology, target.nc), sel.idx + 1) ?? true;
          case "aiport":
            return upFromPort(mcuUplink(topology, target.nc), sel) ?? true;
          case "fs":
            return upFromServer(mcuUplink(topology, target.nc), sel.idx + 1) ?? true;
          default:
            if (selNc !== undefined) return target.nc === selNc;
            return true;
        }
      }
      // "sub" / "link" / "chain": visibility is gated by the endpoint check
      // (both endpoint boxes must be bright) — default to visible here.
      return true;
  }
}
